import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useRoomContext,
} from "@livekit/components-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ConnectionState,
  DisconnectReason,
  RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { gatewayPhone } from "src/gateways/gatewayPhone";
import { useWatchInternalCallNotifications } from "src/notifications/hooks/useWatchInternalCallNotifications";
import { ENotificationType, INotification } from "src/notifications/interfaces";
import { useInternalContacts } from "../_hooks/useInternalContacts";
import { useRequireMicrophoneForCall } from "../_hooks/useRequireMicrophoneForCall";
import { useWebRTCSessionHeartbeat } from "../_hooks/useWebRTCSessionHeartbeat";
import {
  ActiveInternalCall,
  InternalCallNotificationMeta,
  InternalCallPhase,
} from "../_types/internalCall.types";
import { Contact } from "../_types/internalContacts.types";
import { resolveReceiverUserUid } from "../_utils/callRoom.utils";
import {
  formatCallForLabel,
  formatCallFromLabel,
  resolveCompanyDisplayName,
} from "../_utils/internalCallDisplay.utils";
import { resolveInitiateCallErrorMessage } from "../_utils/internalCallError.utils";
import {
  isSameUserUid,
  normalizeInternalCallNotification,
  resolveInternalCallEndMessage,
} from "../_utils/internalCallNotification.utils";
import {
  getContactTitle,
  resolveInternalPeerDisplayName,
} from "../_utils/internalContacts.utils";
import {
  isDuplicateLiveKitIdentityError,
  isFatalLiveKitConnectError,
  isStaleLiveKitConnectError,
  resolveLiveKitConnectErrorMessage,
} from "../_utils/livekitConnect.utils";
import {
  ensureMicrophonePermission,
  getMicrophoneAccessErrorMessage,
} from "../_utils/requestMicrophoneAccess.utils";
import {
  clearCallOriginTab,
  isCallOwner,
  markCallOriginTab,
  webrtcTabSession,
} from "../_utils/webrtcTabSession.utils";

const OUTGOING_RING_TIMEOUT_MS = 40_000;
const LIVEKIT_TEARDOWN_MS = 750;

const LIVEKIT_ROOM_OPTIONS = {
  disconnectOnPageLeave: true,
  // Avoid LiveKit auto-reconnect after DUPLICATE_IDENTITY or local teardown.
  reconnectPolicy: {
    nextRetryDelayInMs: () => null,
  },
} as const;

export type InternalCallView = "incoming" | "outgoing-ringing" | "in-call" | null;

interface InternalWebRTCContextValue {
  phase: InternalCallPhase;
  internalCallView: InternalCallView;
  incomingCall: InternalCallNotificationMeta | null;
  activeCall: ActiveInternalCall | null;
  peerDisplayName: string;
  peerCompanyName: string;
  localCompanyName: string;
  peerSubtitle: string;
  peerLabel: string;
  callDuration: number;
  isMuted: boolean;
  isOnHold: boolean;
  initiateCall: (contact: Contact) => Promise<boolean>;
  acceptIncoming: () => Promise<void>;
  rejectIncoming: () => Promise<void>;
  cancelOutgoing: () => Promise<void>;
  hangup: () => Promise<void>;
  toggleMute: () => void;
  hold: () => Promise<void>;
  resume: () => Promise<void>;
}

const InternalWebRTCContext = createContext<InternalWebRTCContextValue | null>(
  null,
);

export const useInternalWebRTC = (): InternalWebRTCContextValue => {
  const ctx = useContext(InternalWebRTCContext);
  if (!ctx) {
    throw new Error("useInternalWebRTC must be used within InternalWebRTCProvider");
  }
  return ctx;
};

export const useOptionalInternalWebRTC = (): InternalWebRTCContextValue | null =>
  useContext(InternalWebRTCContext);

type MuteControl = {
  toggle: () => Promise<boolean>;
};

type HoldControl = {
  setHold: (hold: boolean, opts?: { restoreMuted?: boolean }) => Promise<void>;
};

const unlockRoomAudioPlayback = (room: Room) => {
  void room.startAudio().catch(() => undefined);
};

const REMOTE_LEAVE_HANGUP_GRACE_MS = 4_000;

const RoomBridge = ({
  callUid,
  onRoom,
  onDisconnected,
  onRemoteParticipantLeft,
  onMuteControl,
  onHoldControl,
  setIsMuted,
  onMediaError,
}: {
  callUid: string;
  onRoom: (room: Room) => void;
  onDisconnected: (room: Room, reason?: DisconnectReason) => void;
  onRemoteParticipantLeft: () => void;
  onMuteControl: (control: MuteControl | null) => void;
  onHoldControl: (control: HoldControl | null) => void;
  setIsMuted: (muted: boolean) => void;
  onMediaError: (message: string) => void;
}) => {
  const room = useRoomContext();
  const onRoomRef = useRef(onRoom);
  const onDisconnectedRef = useRef(onDisconnected);
  const onRemoteParticipantLeftRef = useRef(onRemoteParticipantLeft);
  const onMuteControlRef = useRef(onMuteControl);
  const onHoldControlRef = useRef(onHoldControl);
  const setIsMutedRef = useRef(setIsMuted);
  const onMediaErrorRef = useRef(onMediaError);
  const connectedAtRef = useRef<number | null>(null);
  const sawRemoteRef = useRef(false);

  onRoomRef.current = onRoom;
  onDisconnectedRef.current = onDisconnected;
  onRemoteParticipantLeftRef.current = onRemoteParticipantLeft;
  onMuteControlRef.current = onMuteControl;
  onHoldControlRef.current = onHoldControl;
  setIsMutedRef.current = setIsMuted;
  onMediaErrorRef.current = onMediaError;

  useEffect(() => {
    onRoomRef.current(room);
    connectedAtRef.current =
      room.state === ConnectionState.Connected ? Date.now() : null;
    sawRemoteRef.current = room.remoteParticipants.size > 0;

    const syncMuteState = () => {
      const micPublication = room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      );
      if (!micPublication) {
        setIsMutedRef.current(true);
        return;
      }
      setIsMutedRef.current(micPublication.isMuted);
    };

    const ensureMicrophoneTrack = async () => {
      const permission = await ensureMicrophonePermission();
      if (permission.ok === false) {
        onMediaErrorRef.current(getMicrophoneAccessErrorMessage(permission.reason));
        setIsMutedRef.current(true);
        return;
      }

      try {
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch {
        onMediaErrorRef.current("No se pudo activar el micrófono. Revisa permisos del navegador.");
      }
      syncMuteState();
    };

    const toggle = async () => {
      const currentPublication = room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      );
      const shouldEnable = currentPublication ? currentPublication.isMuted : true;
      try {
        await room.localParticipant.setMicrophoneEnabled(shouldEnable);
      } catch {
        onMediaErrorRef.current("No se pudo cambiar el estado del micrófono.");
      }
      syncMuteState();
      return !room.localParticipant.isMicrophoneEnabled;
    };

    onMuteControlRef.current({ toggle });

    const setRemoteAudioEnabled = (enabled: boolean) => {
      room.remoteParticipants.forEach((participant) => {
        participant.getTrackPublications().forEach((publication) => {
          if (
            publication.kind === Track.Kind.Audio &&
            publication instanceof RemoteTrackPublication
          ) {
            publication.setEnabled(enabled);
          }
        });
      });
    };

    const setHold = async (
      hold: boolean,
      opts?: { restoreMuted?: boolean },
    ) => {
      const micEnabled = hold ? false : !opts?.restoreMuted;
      try {
        await room.localParticipant.setMicrophoneEnabled(micEnabled);
      } catch {
        onMediaErrorRef.current("Couldn't toggle hold for the call.");
      }
      setRemoteAudioEnabled(!hold);
      syncMuteState();
    };

    onHoldControlRef.current({ setHold });

    const handleConnected = () => {
      connectedAtRef.current = Date.now();
      if (room.remoteParticipants.size > 0) {
        sawRemoteRef.current = true;
      }
      unlockRoomAudioPlayback(room);
      void ensureMicrophoneTrack();
    };

    const handleRemoteTrackSubscribed = (
      track: import("livekit-client").RemoteTrack,
    ) => {
      if (track.kind !== Track.Kind.Audio) return;
      sawRemoteRef.current = true;
      unlockRoomAudioPlayback(room);
    };
    const handleTrackMuted = (publication: { source: Track.Source; isMuted: boolean }) => {
      if (publication.source === Track.Source.Microphone) {
        setIsMutedRef.current(publication.isMuted);
      }
    };
    const handleTrackUnmuted = (publication: { source: Track.Source; isMuted: boolean }) => {
      if (publication.source === Track.Source.Microphone) {
        setIsMutedRef.current(publication.isMuted);
      }
    };

    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.TrackSubscribed, handleRemoteTrackSubscribed);
    room.on(RoomEvent.LocalTrackPublished, syncMuteState);
    room.on(RoomEvent.LocalTrackUnpublished, syncMuteState);
    room.localParticipant.on("trackMuted", handleTrackMuted);
    room.localParticipant.on("trackUnmuted", handleTrackUnmuted);

    if (room.state === ConnectionState.Connected) {
      void ensureMicrophoneTrack();
    }

    const handleDisconnect = (reason?: DisconnectReason) => {
      onDisconnectedRef.current(room, reason);
    };
    room.on(RoomEvent.Disconnected, handleDisconnect);

    const handleParticipantDisconnected = () => {
      const connectedAt = connectedAtRef.current;
      const ageMs = connectedAt ? Date.now() - connectedAt : null;
      const remoteCount = room.remoteParticipants.size;
      // Join churn / remount can emit ParticipantDisconnected before media is stable.
      if (!sawRemoteRef.current || remoteCount > 0) {
        return;
      }
      if (ageMs !== null && ageMs < REMOTE_LEAVE_HANGUP_GRACE_MS) {
        return;
      }
      onRemoteParticipantLeftRef.current();
    };
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

    return () => {
      onMuteControlRef.current(null);
      onHoldControlRef.current(null);
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.TrackSubscribed, handleRemoteTrackSubscribed);
      room.off(RoomEvent.LocalTrackPublished, syncMuteState);
      room.off(RoomEvent.LocalTrackUnpublished, syncMuteState);
      room.off(RoomEvent.Disconnected, handleDisconnect);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.localParticipant.off("trackMuted", handleTrackMuted);
      room.localParticipant.off("trackUnmuted", handleTrackUnmuted);

      // Do not room.disconnect() here — LiveKitRoom owns connect lifecycle.
      // Disconnecting on effect re-run caused join churn → hangup within ~100ms.
    };
  }, [callUid, room]);

  return (
    <>
      <RoomAudioRenderer />
      <StartAudio label="Activar audio de la llamada" />
    </>
  );
};

export const InternalWebRTCProvider = ({ children }: { children: ReactNode }) => {
  const { uid, company, firstName, lastName, companies } = useSelector(
    (state: any) => state.userState,
  );
  const companyId = company?.id as number | undefined;
  const localCompanyName = company?.businessName?.trim() ?? "";
  const userCompanies = (companies ?? []) as Array<{
    companyId?: number;
    company?: { id?: number; businessName?: string };
    businessName?: string;
  }>;

  const { showSnackbar } = useGlobalSnackbar();
  const requireMicrophone = useRequireMicrophoneForCall();
  const { contacts } = useInternalContacts();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<InternalCallPhase>("idle");
  const [incomingCall, setIncomingCall] =
    useState<InternalCallNotificationMeta | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveInternalCall | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);

  const roomRef = useRef<Room | null>(null);
  const muteControlRef = useRef<MuteControl | null>(null);
  const holdControlRef = useRef<HoldControl | null>(null);
  const isOnHoldRef = useRef(false);
  const wasMutedBeforeHoldRef = useRef(false);
  const callUidRef = useRef<string | null>(null);
  const acceptingCallUidRef = useRef<string | null>(null);
  const phaseRef = useRef<InternalCallPhase>("idle");
  const activeCallRef = useRef<ActiveInternalCall | null>(null);
  const timerRef = useRef<number | null>(null);
  const outgoingRingTimerRef = useRef<number | null>(null);
  const hangupInFlightRef = useRef(false);
  const acceptInFlightRef = useRef(false);
  const endCallCleanupTimerRef = useRef<number | null>(null);
  const duplicateIdentityRetryRef = useRef(false);
  const awaitingReadySinceRef = useRef<number | null>(null);
  const awaitingReadyCallUidRef = useRef<string | null>(null);
  const [isEndingCall, setIsEndingCall] = useState(false);
  const [liveKitConnectEnabled, setLiveKitConnectEnabled] = useState(false);

  useWebRTCSessionHeartbeat(Boolean(uid && companyId));

  useEffect(() => {
    webrtcTabSession.initialize();
    return () => {
      webrtcTabSession.dispose();
    };
  }, []);

  // Hang up only on real tab/page leave — React cleanup/HMR/StrictMode must not end calls.
  useEffect(() => {
    const endCallOnPageLeave = () => {
      const callUid = callUidRef.current;
      const currentPhase = phaseRef.current;
      if (!callUid || hangupInFlightRef.current) return;

      const isLastTab = webrtcTabSession.isOnlyOpenTab();
      if (!isLastTab) {
        if (currentPhase === "in-call") {
          webrtcTabSession.releaseLiveKit(callUid, { superseded: true });
        }
        return;
      }

      if (currentPhase === "in-call" || currentPhase === "outgoing-ringing") {
        void gatewayPhone.post("/webrtc/call/hangup", { callUid }).catch(() => undefined);
        return;
      }

      if (currentPhase === "incoming-ringing") {
        void gatewayPhone.post("/webrtc/call/reject", { callUid }).catch(() => undefined);
      }
    };

    window.addEventListener("pagehide", endCallOnPageLeave);
    return () => {
      window.removeEventListener("pagehide", endCallOnPageLeave);
      const callUid = callUidRef.current;
      if (callUid && phaseRef.current === "in-call") {
        webrtcTabSession.releaseLiveKit(callUid, { superseded: true });
      }
    };
  }, []);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  const disposeLiveKitRoom = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    if (room.state !== ConnectionState.Disconnected) {
      try {
        await room.disconnect();
      } catch {
        // Best-effort teardown before remounting LiveKitRoom.
      }
    }

    if (roomRef.current === room) {
      roomRef.current = null;
    }
  }, []);

  const resetCallState = useCallback(() => {
    const endedCallUid = callUidRef.current;
    if (endedCallUid) {
      webrtcTabSession.releaseLiveKit(endedCallUid);
      clearCallOriginTab(endedCallUid);
    }

    phaseRef.current = "idle";
    activeCallRef.current = null;
    setPhase("idle");
    setIncomingCall(null);
    setActiveCall(null);
    setCallDuration(0);
    setIsMuted(false);
    setIsOnHold(false);
    isOnHoldRef.current = false;
    wasMutedBeforeHoldRef.current = false;
    callUidRef.current = null;
    hangupInFlightRef.current = false;
    duplicateIdentityRetryRef.current = false;
    awaitingReadySinceRef.current = null;
    awaitingReadyCallUidRef.current = null;
    setIsEndingCall(false);
    setLiveKitConnectEnabled(false);
    void disposeLiveKitRoom();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (outgoingRingTimerRef.current) {
      clearTimeout(outgoingRingTimerRef.current);
      outgoingRingTimerRef.current = null;
    }
    if (endCallCleanupTimerRef.current) {
      clearTimeout(endCallCleanupTimerRef.current);
      endCallCleanupTimerRef.current = null;
    }
    queryClient.invalidateQueries({ queryKey: ["call-history"] });
  }, [disposeLiveKitRoom, queryClient]);

  const finishCallEnd = useCallback(() => {
    if (endCallCleanupTimerRef.current) {
      clearTimeout(endCallCleanupTimerRef.current);
      endCallCleanupTimerRef.current = null;
    }
    resetCallState();
  }, [resetCallState]);

  const disconnectRoom = useCallback(async () => {
    setLiveKitConnectEnabled(false);
    await disposeLiveKitRoom();
  }, [disposeLiveKitRoom]);

  useEffect(() => {
    return webrtcTabSession.onLiveKitReleaseRequested((callUid) => {
      if (callUidRef.current !== callUid) return;
      void disconnectRoom();
    });
  }, [disconnectRoom]);

  useEffect(() => {
    return webrtcTabSession.onCallAcceptedElsewhere((callUid) => {
      if (isCallOwner(callUid)) return;
      if (callUidRef.current !== callUid) return;
      if (
        phaseRef.current !== "incoming-ringing" &&
        phaseRef.current !== "outgoing-ringing"
      ) {
        return;
      }
      resetCallState();
    });
  }, [resetCallState]);

  const recoverFromDuplicateIdentity = useCallback(async (): Promise<boolean> => {
    const callUid = callUidRef.current;
    if (!callUid) return false;

    if (webrtcTabSession.wasSuperseded(callUid) || document.hidden) {
      setLiveKitConnectEnabled(false);
      return true;
    }

    if (duplicateIdentityRetryRef.current) return false;

    duplicateIdentityRetryRef.current = true;
    await webrtcTabSession.prepareLiveKitConnect(callUid);
    setLiveKitConnectEnabled(false);
    await disconnectRoom();
    setLiveKitConnectEnabled(true);
    return true;
  }, [disconnectRoom]);

  const endRemoteCall = useCallback(async () => {
    if (hangupInFlightRef.current) return;
    hangupInFlightRef.current = true;
    setIsEndingCall(true);
    endCallCleanupTimerRef.current = window.setTimeout(() => {
      void disconnectRoom().finally(finishCallEnd);
    }, LIVEKIT_TEARDOWN_MS);
  }, [disconnectRoom, finishCallEnd]);

  const hangup = useCallback(async (reason = "user-or-remote-left") => {
    const callUid = callUidRef.current ?? activeCall?.callUid;
    if (hangupInFlightRef.current) return;
    hangupInFlightRef.current = true;
    setIsEndingCall(true);
    awaitingReadySinceRef.current = null;
    awaitingReadyCallUidRef.current = null;

    try {
      if (callUid) {
        await gatewayPhone.post("/webrtc/call/hangup", { callUid });
      }
    } catch {
      // Peer cleanup still happens when the other side receives InternalCallEnded.
    }

    endCallCleanupTimerRef.current = window.setTimeout(() => {
      void disconnectRoom().finally(finishCallEnd);
    }, LIVEKIT_TEARDOWN_MS);
  }, [activeCall?.callUid, disconnectRoom, finishCallEnd, liveKitConnectEnabled]);

  const attachReadyCall = useCallback(
    async (meta: InternalCallNotificationMeta & { token?: string; livekitUrl?: string }) => {
      if (!uid || !companyId) return;

      if (meta.targetUid && !isSameUserUid(meta.targetUid, uid)) return;

      // Accepted may move caller to in-call before Ready (stop ringtone). Only skip when
      // LiveKit credentials are already attached — otherwise Ready would never connect.
      if (
        callUidRef.current === meta.callUid &&
        phaseRef.current === "in-call" &&
        Boolean(activeCallRef.current?.token) &&
        Boolean(activeCallRef.current?.livekitUrl)
      ) {
        acceptingCallUidRef.current = null;
        return;
      }

      if (!isCallOwner(meta.callUid)) {
        if (
          phaseRef.current === "incoming-ringing" ||
          phaseRef.current === "outgoing-ringing"
        ) {
          resetCallState();
        }
        return;
      }

      setLiveKitConnectEnabled(false);
      duplicateIdentityRetryRef.current = false;

      const role = isSameUserUid(meta.callerUid, uid) ? "caller" : "receiver";
      callUidRef.current = meta.callUid;

      const nextActiveCall: ActiveInternalCall = {
        callUid: meta.callUid,
        roomName: meta.roomName,
        companyId: meta.companyId,
        companyName:
          meta.companyName ??
          resolveCompanyDisplayName(meta.companyId, company, userCompanies),
        receiverCompanyId: meta.receiverCompanyId ?? companyId,
        receiverCompanyName:
          meta.receiverCompanyName ??
          (role === "receiver" ? localCompanyName : undefined),
        callerUid: meta.callerUid,
        receiverUid: meta.receiverUid,
        callerName: meta.callerName,
        receiverName: meta.receiverName,
        token: meta.token,
        livekitUrl: meta.livekitUrl,
        role,
      };

      phaseRef.current = "in-call";
      activeCallRef.current = nextActiveCall;

      setIncomingCall(null);
      setActiveCall(nextActiveCall);
      setPhase("in-call");
      setCallDuration(0);
      acceptingCallUidRef.current = null;

      if (webrtcTabSession.shouldPreferLiveKitConnect(meta.callUid)) {
        await webrtcTabSession.prepareLiveKitConnect(meta.callUid);
        setLiveKitConnectEnabled(true);
      }

    },
    [company, companyId, localCompanyName, resetCallState, uid, userCompanies],
  );

  const handleNotification = useCallback(
    (raw: INotification<InternalCallNotificationMeta>) => {
      const notification = normalizeInternalCallNotification(raw) ?? raw;
      const meta = notification.meta;
      if (!meta?.callUid || !uid) return;

      switch (notification.type) {
        case ENotificationType.InternalCallIncoming:
          if (!isSameUserUid(meta.receiverUid, uid)) return;
          callUidRef.current = meta.callUid;
          setIncomingCall(meta);
          setPhase("incoming-ringing");
          break;

        case ENotificationType.InternalCallAccepted: {
          const callerMatch = isSameUserUid(meta.callerUid, uid);
          const callUidMatch = callUidRef.current === meta.callUid;
          const phaseIsOutgoing = phaseRef.current === "outgoing-ringing";
          // Stop outgoing ringtone immediately; LiveKit connect still waits for Ready
          // so iOS can join an empty room first (working join order).
          if (!callerMatch) return;
          if (!callUidMatch) return;
          if (!phaseIsOutgoing) return;
          phaseRef.current = "in-call";
          setPhase("in-call");
          setCallDuration(0);
          setLiveKitConnectEnabled(false);
          awaitingReadySinceRef.current = Date.now();
          awaitingReadyCallUidRef.current = meta.callUid;
          break;
        }

        case ENotificationType.InternalCallReady:
          if (meta.targetUid && !isSameUserUid(meta.targetUid, uid)) return;
          if (!isSameUserUid(meta.callerUid, uid) && !isSameUserUid(meta.receiverUid, uid)) {
            return;
          }
          awaitingReadySinceRef.current = null;
          awaitingReadyCallUidRef.current = null;
          void attachReadyCall(meta);
          break;

        case ENotificationType.InternalCallTaken:
          if (!isSameUserUid(meta.receiverUid, uid)) return;
          if (isCallOwner(meta.callUid)) return;
          if (
            phaseRef.current === "in-call" &&
            activeCallRef.current?.callUid === meta.callUid
          ) {
            return;
          }
          setIncomingCall(null);
          resetCallState();
          break;

        case ENotificationType.InternalCallRejected:
        case ENotificationType.InternalCallMissed:
        case ENotificationType.InternalCallCanceled: {
          if (!isSameUserUid(meta.callerUid, uid) && !isSameUserUid(meta.receiverUid, uid)) {
            return;
          }
          const message = resolveInternalCallEndMessage(notification.type, uid, meta);
          if (message) {
            showSnackbar(message, "warning");
          }
          void endRemoteCall();
          break;
        }

        case ENotificationType.InternalCallEnded:
          if (!isSameUserUid(meta.callerUid, uid) && !isSameUserUid(meta.receiverUid, uid)) {
            return;
          }
          awaitingReadySinceRef.current = null;
          awaitingReadyCallUidRef.current = null;
          void endRemoteCall();
          break;

        default:
          break;
      }
    },
    [attachReadyCall, endRemoteCall, localCompanyName, resetCallState, showSnackbar, uid],
  );

  useWatchInternalCallNotifications({
    onNotificationArrived: handleNotification,
  });

  useEffect(() => {
    const tryResumeLiveKit = async () => {
      if (
        phaseRef.current !== "in-call" ||
        !activeCallRef.current?.token ||
        !activeCallRef.current.livekitUrl ||
        liveKitConnectEnabled
      ) {
        return;
      }

      const callUid = activeCallRef.current.callUid;
      if (!webrtcTabSession.shouldPreferLiveKitConnect(callUid)) return;

      await webrtcTabSession.prepareLiveKitConnect(callUid);
      setLiveKitConnectEnabled(true);
    };

    const handleFocus = () => {
      void tryResumeLiveKit();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void tryResumeLiveKit();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [liveKitConnectEnabled]);

  useEffect(() => {
    if (phase !== "outgoing-ringing" || !activeCall?.callUid) {
      if (outgoingRingTimerRef.current) {
        clearTimeout(outgoingRingTimerRef.current);
        outgoingRingTimerRef.current = null;
      }
      return;
    }

    const callUid = activeCall.callUid;

    outgoingRingTimerRef.current = window.setTimeout(() => {
      if (phaseRef.current !== "outgoing-ringing") return;
      if (callUidRef.current !== callUid) return;

      showSnackbar("El contacto no respondió a la llamada.", "warning");
      void gatewayPhone.post("/webrtc/call/cancel", { callUid }).catch(() => undefined);
      void endRemoteCall();
    }, OUTGOING_RING_TIMEOUT_MS);

    return () => {
      if (outgoingRingTimerRef.current) {
        clearTimeout(outgoingRingTimerRef.current);
        outgoingRingTimerRef.current = null;
      }
    };
  }, [activeCall?.callUid, endRemoteCall, phase, showSnackbar]);

  useEffect(() => {
    if (phase !== "in-call") {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    if (roomRef.current) {
      unlockRoomAudioPlayback(roomRef.current);
    }

    timerRef.current = window.setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phase, activeCall?.callUid]);

  const initiateCall = useCallback(
    async (contact: Contact): Promise<boolean> => {
      if (!uid || !companyId) {
        console.warn("[InternalWebRTC] Cannot initiate call: missing uid or companyId");
        return false;
      }

      if (phaseRef.current !== "idle" && phaseRef.current !== "ended") {
        showSnackbar("You can only have one active WebRTC call at a time.", "warning");
        return false;
      }

      const receiverUid = resolveReceiverUserUid(contact);
      if (!receiverUid) {
        console.warn("[InternalWebRTC] Cannot initiate call: missing receiver user uid", {
          contactType: contact.type,
          contactUid: contact.uid,
          userUid: contact.userUid,
        });
        showSnackbar("Could not start call with this contact.", "warning");
        return false;
      }

      const canUseMicrophone = await requireMicrophone();
      if (!canUseMicrophone) return false;

      try {
        const { data } = await gatewayPhone.post<{
          callUid: string;
          roomName: string;
          status: "ringing";
        }>("/webrtc/call/initiate", {
          receiverUid,
          receiverName: getContactTitle(contact),
          callerName: `${firstName ?? ""} ${lastName ?? ""}`.trim() || undefined,
          callerCompanyName: localCompanyName || undefined,
        });

        callUidRef.current = data.callUid;
        markCallOriginTab(data.callUid);
        setIncomingCall(null);
        setPhase("outgoing-ringing");
        setActiveCall({
          callUid: data.callUid,
          roomName: data.roomName,
          companyId,
          companyName: localCompanyName,
          callerUid: uid,
          receiverUid,
          callerName: `${firstName ?? ""} ${lastName ?? ""}`.trim() || "You",
          receiverName: getContactTitle(contact),
          role: "caller",
        });
        return true;
      } catch (error: unknown) {
        const { message, severity } = resolveInitiateCallErrorMessage(error);
        showSnackbar(message, severity);
        console.error("[InternalWebRTC] initiateCall failed:", message, error);
        return false;
      }
    },
    [companyId, firstName, lastName, localCompanyName, requireMicrophone, showSnackbar, uid],
  );

  const acceptIncoming = useCallback(async () => {
    if (!incomingCall) return;

    const callUid = incomingCall.callUid;

    if (
      phaseRef.current === "in-call" &&
      callUidRef.current === callUid &&
      activeCallRef.current?.token
    ) {
      return;
    }

    if (acceptInFlightRef.current) return;

    acceptInFlightRef.current = true;
    acceptingCallUidRef.current = callUid;
    markCallOriginTab(callUid);
    webrtcTabSession.broadcastCallAccepted(callUid);

    const canUseMicrophone = await requireMicrophone();
    if (!canUseMicrophone) {
      acceptingCallUidRef.current = null;
      acceptInFlightRef.current = false;
      clearCallOriginTab(callUid);
      return;
    }

    try {
      const { data } = await gatewayPhone.post<{
        callUid: string;
        roomName: string;
        token: string;
        livekitUrl: string;
      }>("/webrtc/call/accept", {
        callUid,
        receiverCompanyName: localCompanyName || undefined,
      });

      await attachReadyCall({
        ...incomingCall,
        callUid: data.callUid,
        roomName: data.roomName,
        token: data.token,
        livekitUrl: data.livekitUrl,
        targetUid: uid,
      });
    } catch {
      acceptingCallUidRef.current = null;
      clearCallOriginTab(callUid);

      const stillInSameCall =
        phaseRef.current === "in-call" && callUidRef.current === callUid;

      if (!stillInSameCall) {
        resetCallState();
      }
    } finally {
      acceptInFlightRef.current = false;
    }
  }, [attachReadyCall, incomingCall, localCompanyName, requireMicrophone, resetCallState, uid]);

  const rejectIncoming = useCallback(async () => {
    if (!incomingCall) return;
    try {
      await gatewayPhone.post("/webrtc/call/reject", {
        callUid: incomingCall.callUid,
      });
    } finally {
      resetCallState();
    }
  }, [incomingCall, resetCallState]);

  const cancelOutgoing = useCallback(async () => {
    const callUid = callUidRef.current ?? activeCall?.callUid;
    try {
      if (callUid) {
        await gatewayPhone.post("/webrtc/call/cancel", { callUid });
      }
    } finally {
      resetCallState();
    }
  }, [activeCall?.callUid, resetCallState]);

  const toggleMute = useCallback(() => {
    if (roomRef.current) {
      unlockRoomAudioPlayback(roomRef.current);
    }
    if (!muteControlRef.current) {
      showSnackbar("El audio de la llamada aún no está listo.", "warning");
      return;
    }
    void muteControlRef.current.toggle();
  }, [showSnackbar]);

  const bindMuteControl = useCallback((control: MuteControl | null) => {
    muteControlRef.current = control;
  }, []);

  const hold = useCallback(async () => {
    if (isOnHoldRef.current) return;
    const room = roomRef.current;
    wasMutedBeforeHoldRef.current = room
      ? !room.localParticipant.isMicrophoneEnabled
      : false;
    isOnHoldRef.current = true;
    setIsOnHold(true);
    await holdControlRef.current?.setHold(true);
  }, []);

  const resume = useCallback(async () => {
    if (!isOnHoldRef.current) return;
    if (roomRef.current) {
      unlockRoomAudioPlayback(roomRef.current);
    }
    isOnHoldRef.current = false;
    setIsOnHold(false);
    await holdControlRef.current?.setHold(false, {
      restoreMuted: wasMutedBeforeHoldRef.current,
    });
  }, []);

  const bindHoldControl = useCallback((control: HoldControl | null) => {
    holdControlRef.current = control;
    if (control && isOnHoldRef.current) {
      void control.setHold(true);
    }
  }, []);

  const handleRemoteParticipantLeft = useCallback(() => {
    if (hangupInFlightRef.current || phaseRef.current !== "in-call") return;
    void hangup("remote-participant-left");
  }, [hangup]);

  const handleRoomDisconnected = useCallback(
    (disconnectedRoom: Room, reason?: DisconnectReason) => {

      if (hangupInFlightRef.current) {
        finishCallEnd();
        return;
      }

      // Ignore disconnect events from a replaced LiveKit room instance.
      if (roomRef.current !== disconnectedRoom) return;

      if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
        void recoverFromDuplicateIdentity().then((handled) => {
          if (handled) return;

          showSnackbar(
            "La sesión de audio se duplicó. Cierra otras pestañas de Flownect e intenta de nuevo.",
            "warning",
          );
          void endRemoteCall();
        });
        return;
      }

      // LiveKit can emit client-initiated disconnects during local remounts
      // (e.g. React StrictMode/dev refresh). Do not force a remote hangup here.
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        return;
      }

      if (reason === DisconnectReason.ROOM_DELETED) {
        showSnackbar("La llamada finalizó.", "info");
        finishCallEnd();
        return;
      }

      const callUid = callUidRef.current;
      if (!callUid) {
        finishCallEnd();
        return;
      }

      hangupInFlightRef.current = true;
      setIsEndingCall(true);
      showSnackbar("Se perdió la conexión con el servidor de audio.", "warning");
      void gatewayPhone
        .post("/webrtc/call/hangup", { callUid })
        .catch(() => undefined)
        .finally(finishCallEnd);
    },
    [endRemoteCall, finishCallEnd, recoverFromDuplicateIdentity, showSnackbar],
  );

  const bindRoom = useCallback((room: Room) => {
    roomRef.current = room;
  }, []);

  const handleMediaError = useCallback(
    (message: string) => {
      showSnackbar(message, "warning");
    },
    [showSnackbar],
  );

  const handleLiveKitError = useCallback(
    (error: Error) => {
      if (isStaleLiveKitConnectError(error)) return;
      if (hangupInFlightRef.current || phaseRef.current !== "in-call") return;

      if (isDuplicateLiveKitIdentityError(error)) {
        void recoverFromDuplicateIdentity().then((handled) => {
          if (handled) return;

          showSnackbar(
            "La sesión de audio se duplicó. Cierra otras pestañas de Flownect e intenta de nuevo.",
            "warning",
          );
          void endRemoteCall();
        });
        return;
      }

      showSnackbar(resolveLiveKitConnectErrorMessage(error), "error");

      if (isFatalLiveKitConnectError(error)) {
        void hangup("fatal-livekit-error");
      }
    },
    [endRemoteCall, hangup, recoverFromDuplicateIdentity, showSnackbar],
  );

  const handleLiveKitDisconnected = useCallback(
    (reason?: DisconnectReason) => {
      const room = roomRef.current;
      if (!room) return;
      handleRoomDisconnected(room, reason);
    },
    [handleRoomDisconnected],
  );

  const peerDisplayName = useMemo(() => {
    if (!uid) return "";

    if (phase === "incoming-ringing" && incomingCall) {
      return resolveInternalPeerDisplayName(
        incomingCall.callerUid,
        contacts,
        incomingCall.callerName,
      );
    }

    if (!activeCall) return "";

    const peerUid =
      activeCall.role === "caller" ? activeCall.receiverUid : activeCall.callerUid;
    const fallbackName =
      activeCall.role === "caller" ? activeCall.receiverName : activeCall.callerName;

    return resolveInternalPeerDisplayName(peerUid, contacts, fallbackName);
  }, [activeCall, contacts, incomingCall, phase, uid]);

  const peerCompanyName = useMemo(() => {
    if (phase === "incoming-ringing" && incomingCall) {
      return (
        incomingCall.companyName?.trim() ||
        resolveCompanyDisplayName(incomingCall.companyId, company, userCompanies)
      );
    }

    if (!activeCall) return "";

    if (activeCall.role === "caller") {
      return (
        activeCall.companyName?.trim() ||
        resolveCompanyDisplayName(activeCall.companyId, company, userCompanies)
      );
    }

    return (
      activeCall.companyName?.trim() ||
      resolveCompanyDisplayName(activeCall.companyId, company, userCompanies)
    );
  }, [activeCall, company, incomingCall, phase, userCompanies]);

  const peerSubtitle = useMemo(
    () => formatCallFromLabel(peerCompanyName),
    [peerCompanyName],
  );

  const peerLabel = useMemo(() => {
    if (phase === "incoming-ringing") {
      return formatCallForLabel(localCompanyName) || "Internal";
    }

    if (phase === "in-call" && activeCall?.role === "receiver") {
      const answeringAs =
        activeCall.receiverCompanyName?.trim() || localCompanyName;
      return formatCallForLabel(answeringAs) || "Internal";
    }

    if (phase === "outgoing-ringing") {
      return formatCallFromLabel(peerCompanyName);
    }

    return "Internal";
  }, [activeCall?.receiverCompanyName, activeCall?.role, localCompanyName, peerCompanyName, phase]);

  const internalCallView: InternalCallView = useMemo(() => {
    if (phase === "incoming-ringing" && incomingCall) return "incoming";
    if (phase === "outgoing-ringing") return "outgoing-ringing";
    if (phase === "in-call" && activeCall?.token && activeCall.livekitUrl) {
      return "in-call";
    }
    return null;
  }, [activeCall, incomingCall, phase]);

  const value = useMemo<InternalWebRTCContextValue>(
    () => ({
      phase,
      internalCallView,
      incomingCall,
      activeCall,
      peerDisplayName,
      peerCompanyName,
      localCompanyName,
      peerSubtitle,
      peerLabel,
      callDuration,
      isMuted,
      isOnHold,
      initiateCall,
      acceptIncoming,
      rejectIncoming,
      cancelOutgoing,
      hangup,
      toggleMute,
      hold,
      resume,
    }),
    [
      phase,
      internalCallView,
      incomingCall,
      activeCall,
      peerDisplayName,
      peerCompanyName,
      localCompanyName,
      peerSubtitle,
      peerLabel,
      callDuration,
      isMuted,
      isOnHold,
      initiateCall,
      acceptIncoming,
      rejectIncoming,
      cancelOutgoing,
      hangup,
      toggleMute,
      hold,
      resume,
    ],
  );

  const liveKitCallUid =
    phase === "in-call" &&
      liveKitConnectEnabled &&
      activeCall?.token &&
      activeCall.livekitUrl
      ? activeCall.callUid
      : null;
  const liveKitToken =
    phase === "in-call" && activeCall?.token ? activeCall.token : null;
  const liveKitUrl =
    phase === "in-call" && activeCall?.livekitUrl ? activeCall.livekitUrl : null;

  return (
    <InternalWebRTCContext.Provider value={value}>
      {children}
      {liveKitCallUid && liveKitToken && liveKitUrl && (
        <LiveKitRoom
          key={liveKitCallUid}
          serverUrl={liveKitUrl}
          token={liveKitToken}
          connect={liveKitConnectEnabled && !isEndingCall}
          options={LIVEKIT_ROOM_OPTIONS}
          connectOptions={{ autoSubscribe: true }}
          audio={false}
          video={false}
          onError={handleLiveKitError}
          onDisconnected={handleLiveKitDisconnected}
          style={{ display: "none" }}
        >
          <RoomBridge
            callUid={liveKitCallUid}
            onRoom={bindRoom}
            onMuteControl={bindMuteControl}
            onHoldControl={bindHoldControl}
            setIsMuted={setIsMuted}
            onDisconnected={handleRoomDisconnected}
            onRemoteParticipantLeft={handleRemoteParticipantLeft}
            onMediaError={handleMediaError}
          />
        </LiveKitRoom>
      )}
    </InternalWebRTCContext.Provider>
  );
};
